/*
 * @Author: Hong.Zhang
 * @Date: 2024-12-13 14:34:50
 * @Description:
 */
import SompoSquare from '@/assets/icon/sompo_square.png';
// import ZurichSquare from '@/assets/icon/zurich_square.png';
import VictoriaSquare from '@/assets/icon/victoria_square.png';
import ZurichSyariahSquare from '@/assets/icon/zurich_syariah_square.png';
import { isAgeWithinRange } from '@/utils/age';
import { SERVER_DOB_FORMATTER } from '@/utils/date';
import { jump2ThirdPartyPage } from '@/utils/debug';
import dayjs from 'dayjs';
import {
  AGE_RANGE_MOTOLITE,
  AGE_RANGE_PA_HOME_PROTECTION,
  AGE_RANGE_PA_HOSPITAL_CASH,
  AGE_RANGE_ZURICH_SYARIAH_PA,
  MOTOLITE_POLICY_WORDING,
  PA_HOME_PROTECTION_POLICY_WORDING,
  PA_HOSPITAL_CASH_POLICY_WORDING,
  PDF_VIEWER_URL,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
  TRAVEL_DOMESTIC_POLICY_WORDING,
  ZURICH_SYARIAH_PA_POLICY_WORDING,
} from '.';

export const PRODUCT_NAMES = {
  TRAVEL_DOMESTIC: 'travel_domestic',
  ZURICH_SYARIAH_PA: 'zurich_syariah_pa',
  PA_HOSPITAL_CASH: 'pa_hospital_cash',
  PA_HOME_PROTECTION: 'pa_home_protection',
  MOTOLITE: 'motolite',
};

export const PRODUCT_KEY_2_PRODUCT_NAME: Record<string, string> = {
  '7115f78a-6bab-48d3-aaf3-6bc8b5e877ea': PRODUCT_NAMES.TRAVEL_DOMESTIC,
  'fda5e0ac-0d42-4a91-99c9-2f05d5c69802': PRODUCT_NAMES.ZURICH_SYARIAH_PA,
  'c0d770e8-3240-46fc-9ded-e755ceb1f97e': PRODUCT_NAMES.PA_HOSPITAL_CASH,
  'd7555958-eca4-40f8-b892-7ff78fb98bef': PRODUCT_NAMES.PA_HOME_PROTECTION,
  '4d7e62c9-45d3-4c6c-b04e-75b30c81ef71': PRODUCT_NAMES.MOTOLITE,
};

export const PRODUCT_KEY_2_ORDER: Record<string, number> = {
  '7115f78a-6bab-48d3-aaf3-6bc8b5e877ea': 4,
  'fda5e0ac-0d42-4a91-99c9-2f05d5c69802': 1,
  'c0d770e8-3240-46fc-9ded-e755ceb1f97e': 2,
  'd7555958-eca4-40f8-b892-7ff78fb98bef': 3,
};

export const NEXT_PAGE_ARRANGER: Record<string, Record<string, string>> = {
  '/product-list': {
    [PRODUCT_NAMES.TRAVEL_DOMESTIC]: '/fast-quote',
    [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: '/product-detail',
    [PRODUCT_NAMES.PA_HOSPITAL_CASH]: '/product-detail',
    [PRODUCT_NAMES.PA_HOME_PROTECTION]: '/product-detail',
    [PRODUCT_NAMES.MOTOLITE]: '/product-detail',
  },
  '/auth': {
    [PRODUCT_NAMES.TRAVEL_DOMESTIC]: '/fill-form?step=0',
    [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: '/summary',
    [PRODUCT_NAMES.PA_HOSPITAL_CASH]: '/summary',
    [PRODUCT_NAMES.PA_HOME_PROTECTION]: '/summary',
    [PRODUCT_NAMES.MOTOLITE]: '/fill-form?step=0',
  },
  '/auth-success': {
    [PRODUCT_NAMES.TRAVEL_DOMESTIC]: '/fill-form?step=0',
    [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: '/summary',
    [PRODUCT_NAMES.PA_HOSPITAL_CASH]: '/summary',
    [PRODUCT_NAMES.PA_HOME_PROTECTION]: '/summary',
    [PRODUCT_NAMES.MOTOLITE]: '/fill-form?step=0',
  },
};

export const ENABLED_SECTIONS = {
  BENEFIT: 'benefit',
  // summary page
  PERSON_INFO: 'person_info',
  TRAVEL_INFO: 'travel_info',
  TRAVELERS_INFO: 'travelers_info',
  SOI_INFO: 'soi_info',
};

export const getProductInfoMap = (formatMessage?: any) => ({
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: {
    insurerLogo: SompoSquare,
    name: formatMessage
      ? formatMessage({ id: 'Domestic Travel Insurance' })
      : null,
    desc: formatMessage
      ? formatMessage({
          id: 'When traveling domestically, travel insurance can provide protection against unexpected travel incidents.',
        })
      : null,
    category: 'Domestic',
    planCardWidth: { width: '45%' },
  },
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: {
    insurerLogo: ZurichSyariahSquare,
    name: formatMessage ? formatMessage({ id: 'Personal Accident' }) : null,
    desc: formatMessage
      ? formatMessage({
          id: 'This insurance protects you in case of an accident that causes injury, leading to disability, medical costs, or death. If such an event occurs, you or your designated beneficiaries will receive the benefits mentioned in the policy.',
        })
      : null,
    category: 'PA',
    planCardWidth: { width: '36%' },
  },
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: {
    insurerLogo: VictoriaSquare,
    name: formatMessage
      ? formatMessage({ id: 'PA Plus Hospital Cash Plan' })
      : null,
    desc: formatMessage
      ? formatMessage({
          id: 'This insurance provides coverage in case of accidental death and offers a daily allowance of Rp500,000 for up to 5 days if you are hospitalized.',
        })
      : null,
    category: 'PA',
    planCardWidth: { width: '50%' },
  },
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: {
    insurerLogo: VictoriaSquare,
    name: formatMessage
      ? formatMessage({ id: 'PA and Home Protection' })
      : null,
    desc: formatMessage
      ? formatMessage({
          id: 'This insurance provides coverage in case of death, permanent disability, medical expenses due to accidents, and compensation for home damage caused by fire or theft with forced entry.',
        })
      : null,
    category: 'PA',
    planCardWidth: { width: '50%' },
  },
  [PRODUCT_NAMES.MOTOLITE]: {
    insurerLogo: ZurichSyariahSquare,
    name: formatMessage ? formatMessage({ id: 'Motolite Insurance' }) : null,
    desc: formatMessage
      ? formatMessage({
          id: 'Feel more comfortable and safer on your trips with protection from Motolite Micro Insurance from Zurich Insurance. This micro insurance product provides affordable protection for your two-wheel motorbike and the driver. With Motolite Micro Insurance, you can have the assurance that you and your vehicle are covered against unexpected expenses.',
        })
      : null,
    category: 'Motorcycle',
    planCardWidth: { width: '50%' },
  },
});

export const ELIGIBLE_CHECK_FN_MAP = {
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: (profile: any) => {
    return isAgeWithinRange(
      dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
      AGE_RANGE_PA_HOSPITAL_CASH,
    );
  },
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: (profile: any) => {
    return isAgeWithinRange(
      dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
      AGE_RANGE_ZURICH_SYARIAH_PA,
    );
  },
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: (profile: any) => {
    return isAgeWithinRange(
      dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
      AGE_RANGE_PA_HOSPITAL_CASH,
    );
  },
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: (profile: any) => {
    return isAgeWithinRange(
      dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
      AGE_RANGE_PA_HOME_PROTECTION,
    );
  },
  [PRODUCT_NAMES.MOTOLITE]: (profile: any) => {
    return isAgeWithinRange(
      dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
      AGE_RANGE_MOTOLITE,
    );
  },
};

export const getHighlightMap = (formatMessage: any) => ({
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: [
    {
      title: formatMessage({ id: 'What does it cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Personal accident',
          }),
          value: formatMessage({
            id: 'This insurance covers damages resulting from permanent disability or loss of life due to accidents.',
          }),
        },
        {
          label: formatMessage({
            id: 'Medical expenses',
          }),
          value: formatMessage({
            id: 'Medical treatments, hospitalization, and medical evacuation expenses are covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Loss of baggage and personal belongings',
          }),
          value: formatMessage({
            id: 'Loss or theft of baggage and personal belongings is covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Trip cancellation',
          }),
          value: formatMessage({
            id: 'Trip cancellation due to illness, injury, or personal emergencies is covered.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What it does not cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Traveling as a pilot or crew member',
          }),
          value: formatMessage({
            id: 'Only registered passengers on public or private air or sea transport are covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'War or Civil Unrest',
          }),
          value: formatMessage({
            id: 'This insurance does not cover you for any type of war, invasion, or takeover of power.',
          }),
        },
        {
          label: formatMessage({
            id: 'Nuclear, biological, chemical, and radiological terrorism',
          }),
          value: formatMessage({
            id: 'This insurance does not cover you for nuclear, biological, chemical, or radiological terrorism.',
          }),
        },
        {
          label: formatMessage({
            id: 'Damage or loss of belongings by authorities',
          }),
          value: formatMessage({
            id: 'You are not covered for property damage or loss caused by customs or other authorities.',
          }),
        },
        {
          label: formatMessage({
            id: 'Claims arising from unlawful acts',
          }),
          value: formatMessage({
            id: 'This insurance does not cover claims arising from government intervention, prohibition, regulation, or unlawful acts.',
          }),
        },
        {
          label: formatMessage({
            id: 'Pre-existing conditions',
          }),
          value: formatMessage({
            id: 'Death, illness, or injury caused by pre-existing conditions are not covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Travel for medical treatment',
          }),
          value: formatMessage({
            id: 'This insurance does not cover travel for medical treatments.',
          }),
        },
        {
          label: formatMessage({
            id: 'Health supplements and vitamins',
          }),
          value: formatMessage({
            id: 'Expenses for health supplements, vitamins, probities, and skincare products are not covered. ',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What do we need to know' }),
      contents: [
        formatMessage({
          id: 'The maximum coverage for a short trip is 180 days.',
        }),
        formatMessage({
          id: 'Once the insurance certificate has been issued, cancellation of a Short Trip Policy will not result in a refund.',
        }),
        formatMessage({
          id: 'To be covered, you must be aged between 0 and 70.',
        }),
        formatMessage({
          id: 'Dual coverage requires both people to travel together on the same schedule for the entire trip. They do not have to be related.',
        }),
        formatMessage({
          id: 'Family coverage is available for 1 or 2 adults with a maximum of 3 children. The adults do not have to be related, but the children must be related to at least 1 adult.',
        }),
        formatMessage({
          id: 'For domestic travel, the destination must be at least 100 km away from your home.',
        }),
      ],
    },
  ],
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: [
    {
      title: formatMessage({ id: 'What does it cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Sports activities risk',
          }),
          value: formatMessage({
            id: 'Covers death or injury from sports activities in amateur capacity, including fencing and scuba diving (up to 30 meters) with a PADI certification or qualified instructor.',
          }),
        },
        {
          label: formatMessage({
            id: 'Accidental death due to food and drink poisoning and choking',
          }),
          value: formatMessage({
            id: 'If you die from food or drink poisoning, including choking, without any element of intentional harm.',
          }),
        },
        {
          label: formatMessage({
            id: 'Motorboat and motorcycling risk',
          }),
          value: formatMessage({
            id: 'Covers you while driving or riding motorboats and motorcycles, except during speed competitions or races.',
          }),
        },
        {
          label: formatMessage({
            id: 'Suffocation and drowning',
          }),
          value: formatMessage({
            id: 'If you die or are permanently disabled from drowning or suffocation by smoke, gas, or poisonous fumes.',
          }),
        },
        {
          label: formatMessage({
            id: 'Kidnapping risk',
          }),
          value: formatMessage({
            id: 'Covers death or disablement from kidnapping (excluding ransom or related expenses)',
          }),
        },
        {
          label: formatMessage({
            id: 'Riot, strike, civil commotion, terrorism, and sabotage',
          }),
          value: formatMessage({
            id: 'Covers injuries resulting from riot, strike, civil commotion, terrorism, and sabotage',
          }),
        },
        {
          label: formatMessage({
            id: 'Chartered and other unscheduled flight',
          }),
          value: formatMessage({
            id: 'Covers accidents if you are a passenger in from a fully licensed single-engine aircraft (except helicopter)',
          }),
        },
        {
          label: formatMessage({
            id: 'Bomb threat, hijack, murder and assault',
          }),
          value: formatMessage({
            id: 'This policy covers injuries or death from bomb threats, hijacking, murder, and assault, as long as you didn’t provoke the incident and couldn’t reasonably avoid it.',
          }),
        },
        {
          label: formatMessage({
            id: 'Exposure and disappearance',
          }),
          value: formatMessage({
            id: 'If you suffer a loss after unavoidable exposure from an accident, the loss will be covered. Also if you go missing during the policy period and is not found within 365 days, the death benefit will be paid.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What it does not cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Duplication of cover clause',
          }),
          value: formatMessage({
            id: 'If the participant has more than one policy issued by Zurich for the same event, you will only be covered from one policy with highest benefit limit',
          }),
        },
        {
          label: formatMessage({
            id: 'Exclusions for occupations and activities',
          }),
          value: formatMessage({
            id: 'This policy excludes coverage for military or police service, aircraft crew, ship crew,  professional athletes, offshore workers, underground activities worker, and underwater activities worker.',
          }),
        },
        {
          label: formatMessage({
            id: 'Exclusions for health conditions',
          }),
          value: formatMessage({
            id: 'Excludes coverage for pregnancy, childbirth, miscarriage, abortion, insanity, sexually transmitted diseases, HIV/AIDS, ARC and pre-existing physical or mental conditions',
          }),
        },
        {
          label: formatMessage({
            id: 'Exclusions for intentional self-injury',
          }),
          value: formatMessage({
            id: 'This policy excludes coverage for suicide, self-injury, sickness not from injury, and injuries from gradual causes',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What do we need to know' }),
      contents: [
        {
          label: formatMessage({
            id: 'Eligibility Requirements',
          }),
          value: formatMessage({
            id: 'The participant must be Indonesian citizens or residents with valid permits to stay or work in Indonesia',
          }),
        },
        {
          label: formatMessage({
            id: 'Age Eligibility',
          }),
          value: formatMessage({
            id: 'The participant must be between 16 and 65 years old',
          }),
        },
        {
          label: formatMessage({
            id: 'Eligible Occupation',
          }),
          value: formatMessage({
            id: 'Coverage is available for occupations including non-dangerous work, office workers, technicians, drivers, security guards, and transport workers.',
          }),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: [
    {
      title: formatMessage({ id: 'What does it cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Accidental Death',
          }),
          value: formatMessage({
            id: 'Covers accidental death during the coverage period.',
          }),
        },
        {
          label: formatMessage({
            id: 'Hospitalization Daily Allowance',
          }),
          value: formatMessage({
            id: 'Provides a daily allowance of Rp500,000 for up to 5 days if hospitalized during the coverage period.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What it does not cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Travel as a Pilot or Crew Member',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents that occur while the insured is participating in air traffic activities, except as a lawful passenger on a commercial flight operated by a licensed airline.',
          }),
        },
        {
          label: formatMessage({
            id: 'High-Risk Sports',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents resulting from engaging in activities such as boxing, wrestling, martial arts, rugby, hockey, ice or snow sports, mountaineering, cave exploration, hunting, solo sailing, or participating in speed or skill-based motor vehicle or motorcycle races.',
          }),
        },
        {
          label: formatMessage({
            id: 'Criminal Acts',
          }),
          value: formatMessage({
            id: 'Accidents or injuries resulting from participation in criminal activities or intentional illegal acts are not covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Violation of Laws',
          }),
          value: formatMessage({
            id: 'This insurance does not cover any injuries or losses resulting from actions that violate any applicable laws or regulations.',
          }),
        },
        {
          label: formatMessage({
            id: 'Pre-existing Health Conditions',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents or illnesses arising from pre-existing health conditions, including but not limited to hernia, epilepsy, sunstroke, or other chronic health issues.',
          }),
        },
        {
          label: formatMessage({
            id: 'Infectious Diseases',
          }),
          value: formatMessage({
            id: 'This insurance does not cover illnesses caused by viral, bacterial, or parasitic infections, such as malaria, typhus, leptospirosis, or diseases transmitted by insect bites.',
          }),
        },
        {
          label: formatMessage({
            id: 'Complications from Existing Medical Conditions.',
          }),
          value: formatMessage({
            id: 'Accidents aggravated by pre-existing medical conditions, such as diabetes or poor blood circulation, are not covered under this insurance. Compensation will not exceed the amount that would have been paid if no such aggravating factors were present.',
          }),
        },
        {
          label: formatMessage({
            id: 'Military or Police Duties',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents that occur while the insured is engaged in military or police duties, or related activities, unless approved by the insurer.',
          }),
        },
        {
          label: formatMessage({
            id: 'War and Civil Unrest',
          }),
          value: formatMessage({
            id: 'Accidents caused directly or indirectly by war, insurrection, riots, strikes, terrorism, military force, or civil unrest are excluded from coverage.',
          }),
        },
        {
          label: formatMessage({
            id: 'Detainment or Deportation',
          }),
          value: formatMessage({
            id: 'Accidents that occur while the insured is detained or exiled due to deportation, military orders, or political actions are not covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Nuclear, Biological, Chemical, or Radiological Hazards',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents or illnesses resulting from nuclear reactions, radiation, or biological, chemical, or radiological terrorism.',
          }),
        },
        {
          label: formatMessage({
            id: 'Preventive or Mitigative Costs',
          }),
          value: formatMessage({
            id: 'This insurance does not cover expenses incurred to prevent or reduce losses, unless explicitly approved by the insurer.',
          }),
        },
        {
          label: formatMessage({
            id: 'Intentional Acts',
          }),
          value: formatMessage({
            id: 'No coverage is provided for accidents resulting from intentional actions, planned or desired by the insured or the beneficiary, except in cases where the insured is acting in self-defense or to protect others within legal bounds.',
          }),
        },
        {
          label: formatMessage({
            id: 'HIV and AIDS-related Illnesses',
          }),
          value: formatMessage({
            id: 'The insurance does not cover any medical treatments, claims, or expenses related to HIV, AIDS, or similar conditions, including HIV-related infections.',
          }),
        },
        {
          label: formatMessage({
            id: 'Policy Wording',
          }),
          value: formatMessage({
            id: 'Please see the full exclusion in the policy wording here.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What do we need to know' }),
      contents: [
        {
          label: formatMessage({
            id: 'Age Eligibility',
          }),
          value: formatMessage({
            id: 'This insurance coverage is only available to individuals aged 17 years and above and up to 60 years old.',
          }),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: [
    {
      title: formatMessage({ id: 'What does it cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Accidental Death',
          }),
          value: formatMessage({
            id: 'Coverage will be provided if the Insured passes away as a direct result of an accident covered under the policy',
          }),
        },
        {
          label: formatMessage({
            id: 'Permanent Disability',
          }),
          value: formatMessage({
            id: 'Coverage will be provided if the Insured suffers permanent disability as a direct result of an accident covered by the policy',
          }),
        },
        {
          label: formatMessage({
            id: 'Medical Expenses',
          }),
          value: formatMessage({
            id: 'Coverage will be provided for the reimbursement of treatment and/or medical expenses incurred in the process of healing or recovering from illness or injury caused by an accident covered by the policy.',
          }),
        },
        {
          label: formatMessage({
            id: 'Home protection due to fire',
          }),
          value: formatMessage({
            id: 'Coverage will be provided for damage or loss to the Insured’s home caused by fire',
          }),
        },
        {
          label: formatMessage({
            id: 'Home protection due to bulglary with forcible entry',
          }),
          value: formatMessage({
            id: 'Coverage will be provided for loss or damage to the Insured’s home and its contents resulting from burglary or theft involving forcible and violent entry',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What it does not cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Travel as a Pilot or Crew Member',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents that occur while the insured is participating in air traffic activities, except as a lawful passenger on a commercial flight operated by a licensed airline.',
          }),
        },
        {
          label: formatMessage({
            id: 'High-Risk Sports',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents resulting from engaging in activities such as boxing, wrestling, martial arts, rugby, hockey, ice or snow sports, mountaineering, cave exploration, hunting, solo sailing, or participating in speed or skill-based motor vehicle or motorcycle races.',
          }),
        },
        {
          label: formatMessage({
            id: 'Criminal Acts',
          }),
          value: formatMessage({
            id: 'Accidents or injuries resulting from participation in criminal activities or intentional illegal acts are not covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Violation of Laws',
          }),
          value: formatMessage({
            id: 'This insurance does not cover any injuries or losses resulting from actions that violate any applicable laws or regulations.',
          }),
        },
        {
          label: formatMessage({
            id: 'Pre-existing Health Conditions',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents or illnesses arising from pre-existing health conditions, including but not limited to hernia, epilepsy, sunstroke, or other chronic health issues.',
          }),
        },
        {
          label: formatMessage({
            id: 'Infectious Diseases',
          }),
          value: formatMessage({
            id: 'This insurance does not cover illnesses caused by viral, bacterial, or parasitic infections, such as malaria, typhus, leptospirosis, or diseases transmitted by insect bites.',
          }),
        },
        {
          label: formatMessage({
            id: 'Complications from Existing Medical Conditions',
          }),
          value: formatMessage({
            id: 'Accidents aggravated by pre-existing medical conditions, such as diabetes or poor blood circulation, are not covered under this insurance. Compensation will not exceed the amount that would have been paid if no such aggravating factors were present.',
          }),
        },
        {
          label: formatMessage({
            id: 'Military or Police Duties',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents that occur while the insured is engaged in military or police duties, or related activities, unless approved by the insurer.',
          }),
        },
        {
          label: formatMessage({
            id: 'War and Civil Unrest',
          }),
          value: formatMessage({
            id: 'Accidents caused directly or indirectly by war, insurrection, riots, strikes, terrorism, military force, or civil unrest are excluded from coverage.',
          }),
        },
        {
          label: formatMessage({
            id: 'Detainment or Deportation',
          }),
          value: formatMessage({
            id: 'Accidents that occur while the insured is detained or exiled due to deportation, military orders, or political actions are not covered.',
          }),
        },
        {
          label: formatMessage({
            id: 'Nuclear, Biological, Chemical, or Radiological Hazards',
          }),
          value: formatMessage({
            id: 'This insurance does not cover accidents or illnesses resulting from nuclear reactions, radiation, or biological, chemical, or radiological terrorism.',
          }),
        },
        {
          label: formatMessage({
            id: 'Preventive or Mitigative Costs',
          }),
          value: formatMessage({
            id: 'This insurance does not cover expenses incurred to prevent or reduce losses, unless explicitly approved by the insurer.',
          }),
        },
        {
          label: formatMessage({
            id: 'Intentional Acts',
          }),
          value: formatMessage({
            id: 'No coverage is provided for accidents resulting from intentional actions, planned or desired by the insured or the beneficiary, except in cases where the insured is acting in self-defense or to protect others within legal bounds.',
          }),
        },
        {
          label: formatMessage({
            id: 'HIV and AIDS-related Illnesses',
          }),
          value: formatMessage({
            id: 'The insurance does not cover any medical treatments, claims, or expenses related to HIV, AIDS, or similar conditions, including HIV-related infections.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What do we need to know' }),
      contents: [
        {
          label: formatMessage({
            id: 'Age Eligibility',
          }),
          value: formatMessage({
            id: 'This insurance coverage is only available to individuals aged 17 years and above and up to 60 years old.',
          }),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.MOTOLITE]: [
    {
      title: formatMessage({ id: 'What does it cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Loss of motorcycle',
          }),
          value: formatMessage({
            id: 'This insurance will cover the financial loss if you lose your motorcycle, are involved in an accident, or suffer a loss greater than 75% of the sum insured. You will be covered up to Rp 2,500,000.',
          }),
        },
        {
          label: formatMessage({
            id: 'Permanent disability or accidental death of a third party',
          }),
          value: formatMessage({
            id: 'This insurance will cover the financial loss if a third party suffers total permanent disability or death as a result of the motorcycle. You will be compensated up to Rp 2,500,000.',
          }),
        },
        {
          label: formatMessage({
            id: 'Permanent disability or accidental death',
          }),
          value: formatMessage({
            id: 'This insurance will cover the financial loss if you suffer total permanent disability or accidental death. You will be covered up to Rp 2,500,000.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What it does not cover' }),
      contents: [
        {
          label: formatMessage({
            id: 'Accident or injury caused deliberately',
          }),
          value: formatMessage({
            id: 'Damage is not covered if the motorcycle driver, the insured person, or their family deliberately orders or knows of any violations of law.',
          }),
        },
        {
          label: formatMessage({
            id: 'Damage due to unforeseen events',
          }),
          value: formatMessage({
            id: 'The insurance does not cover damage to the motorcycle is caused by riots, strikes, lockouts, brawling, civil commotion, insurrection, usurped power, revolution, rebellion, military power, invasion, civil war, war and hostilities, subversive acts, terrorism, sabotage, looting.',
          }),
        },
        {
          label: formatMessage({
            id: 'Damage due to nuclear reactions',
          }),
          value: formatMessage({
            id: 'The insurance does not cover damage caused by a nuclear reaction, including nuclear radiation, ionization, fusion, fission, or radioactive contamination.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'What do we need to know' }),
      contents: [
        formatMessage({
          id: 'The insured person must be between 17 and 60 years old.',
        }),
        formatMessage({
          id: 'The insured person must have an active driving license (SIM).',
        }),
        formatMessage({
          id: 'There is a waiting period of at least 15 days from the start of the policy before claims can be made.',
        }),
        formatMessage({
          id: 'Each motorcycle can only have one policy.',
        }),
        formatMessage({
          id: 'The insured person can purchase a maximum of four policies for four different motorcycles.',
        }),
        formatMessage({
          id: 'At the time of insuring the motorcycle, the motorcycle must be a maximum of 7 years old using the year of manufacture as the starting point.',
        }),
        formatMessage({
          id: 'If there is more than one loss in a single incident, the maximum insurance compensation is Rp 7,500,000.',
        }),
      ],
    },
  ],
});

export const getClaimMap = (formatMessage: any) => ({
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: [
    {
      title: formatMessage({ id: 'How to make a cashless claim' }),
      contents: [
        {
          label: formatMessage({
            id: 'Contact Sompo (emergency only)',
          }),
          value: formatMessage({
            id: 'If you are suffering from either food poisoning, or hypothermia or have been in an accident and need emergency medical care, call Sompo directly on +62 212 997 8909 or Whatsapp them on +62 811 131 4051 for immediate assistance.',
          }),
        },
        {
          label: formatMessage({
            id: 'Provide your policy details to Sompo',
          }),
          value: formatMessage({
            id: 'Particulars handy when talking to Sompo customer care and they can assist you with the cashless claim. ',
          }),
        },
        {
          label: formatMessage({
            id: 'Guided assistance',
          }),
          value: formatMessage({
            id: 'Sompo’s emergency assistance will arrange the appropriate cover and care for you. They will do the necessary steps to ensure you covered, where appropriate, for you needs.',
          }),
        },
      ],
    },
    {
      title: formatMessage({ id: 'How to file a reimbursement claim' }),
      contents: [
        {
          label: formatMessage({
            id: 'Contact Igloo Care Indonesia (others)',
          }),
          value: formatMessage({
            id: 'Email cs.id@iglooinsure.com and the team will guide you through the process of making a claim.',
          }),
        },
        {
          label: formatMessage({
            id: 'Prepare required claim documents',
          }),
          value: formatMessage({
            id: 'Prepare the required supporting claim documents. Igloo Care Indonesia will help you with understanding which documents you require to file the claim.',
          }),
        },
        {
          label: formatMessage({
            id: 'Wait for the claim to be processed',
          }),
          value: formatMessage({
            id: 'Claim will be processed as fast as possible. We will work with you and Sompo to ensure the process is smooth. Disbursement can take up to 14 working days or maximum 30 calendar days after the approval settlement benefits payment.insurer.',
          }),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: [
    {
      title: formatMessage({ id: 'How to claim?' }),
      contents: [
        {
          label: formatMessage({
            id: 'Claim must be notified within 30 calendar days after incident',
          }),
        },
        {
          label: formatMessage({
            id: 'In the case of death due to Accident:',
          }),
          value: (
            <div>
              <div>
                <div>1. {formatMessage({ id: 'Completed claim form' })}</div>
                <div>
                  2. {formatMessage({ id: 'Participant’s identity card' })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Copy of accident statement letter issued by the police force',
                  })}
                </div>
                <div>
                  4.{' '}
                  {formatMessage({
                    id: 'Copy of valid driving License (in the case of traffic accident)',
                  })}
                </div>
                <div>
                  {' '}
                  5.{' '}
                  {formatMessage({
                    id: 'Copy of family certificate of beneficiary',
                  })}
                </div>
                <div>
                  6.{' '}
                  {formatMessage({
                    id: 'Death certificate issued by the hospital and the district (for death claims)',
                  })}
                </div>
                <div>7. {formatMessage({ id: 'Beneficiary statement' })}</div>
                <div>
                  8.{' '}
                  {formatMessage({
                    id: 'Other documents (if necessary) which will be communicated further',
                  })}
                </div>
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'In case of disablement:',
          }),
          value: (
            <div>
              <div>
                {formatMessage({
                  id: 'Rontgen photo and receipt of rontgen photo',
                })}
              </div>
              <div>
                {formatMessage({
                  id: 'Statement from the Hospital (For Permanent Disablement).',
                })}
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'In case of medical expenses:',
          }),
          value: (
            <div>
              <div>
                1. {formatMessage({ id: 'Statement from policyholder' })}
              </div>
              <div>2. {formatMessage({ id: 'Copy of ID' })}</div>
              <div>
                3.{' '}
                {formatMessage({
                  id: 'Copy of driving license (for traffic accident)',
                })}
              </div>
              <div>
                4.{' '}
                {formatMessage({
                  id: 'Statement issued by the police force (for traffic accident, criminal acts, etc)',
                })}
              </div>
              <div>
                5.{' '}
                {formatMessage({
                  id: 'Statement issued by doctor',
                })}
              </div>
              <div>
                6.{' '}
                {formatMessage({
                  id: 'Original copy of medical receipt',
                })}
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'Claim document must be submitted within 180 calendar days after claim notification and claim payment will be paid within 10 (ten) working days after claim document is being received completely.',
          }),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: [
    {
      title: formatMessage({ id: 'How to claim?' }),
      contents: [
        {
          label: formatMessage({
            id: 'The insured is required to submit the following documents:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Claim form, along with a detailed accident report.',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'Statements from witnesses, along with any supporting evidence.',
                  })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Original policy or a copy.',
                  })}
                </div>
                <div>
                  4.{' '}
                  {formatMessage({
                    id: 'Copy of KTP',
                  })}
                </div>
                <div>
                  5.{' '}
                  {formatMessage({
                    id: 'Medical report (Visum) from the treating doctor.',
                  })}
                </div>
                <div>
                  6.{' '}
                  {formatMessage({
                    id: 'In case of death:',
                  })}
                  <div className="ml-4">
                    a.{' '}
                    {formatMessage({
                      id: 'Death examination report (Visum et Repertum).',
                    })}
                  </div>
                  <div className="ml-4">
                    b.{' '}
                    {formatMessage({
                      id: 'Copy of the death certificate issued by local authorities."',
                    })}
                  </div>
                </div>
              </div>
            </div>
          ),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: [
    {
      title: formatMessage({ id: 'How to claim?' }),
      contents: [
        {
          label: formatMessage({
            id: 'The insured is required to submit the following documents:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Claim form, along with a detailed accident report.',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'Statements from witnesses, along with any supporting evidence.',
                  })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Original policy or a copy.',
                  })}
                </div>
                <div>
                  4.{' '}
                  {formatMessage({
                    id: 'Copy of KTP',
                  })}
                </div>
                <div>
                  5.{' '}
                  {formatMessage({
                    id: 'Medical report (Visum) from the treating doctor.',
                  })}
                </div>
                <div>
                  6.{' '}
                  {formatMessage({
                    id: 'In case of death:',
                  })}
                  <div className="ml-4">
                    a.{' '}
                    {formatMessage({
                      id: 'Death examination report (Visum et Repertum).',
                    })}
                  </div>
                  <div className="ml-4">
                    b.{' '}
                    {formatMessage({
                      id: 'Copy of the death certificate issued by local authorities."',
                    })}
                  </div>
                </div>
              </div>
            </div>
          ),
        },
      ],
    },
  ],
  [PRODUCT_NAMES.MOTOLITE]: [
    {
      title: formatMessage({ id: 'How to claim?' }),
      contents: [
        {
          label: formatMessage({
            id: 'Motorcycle Loss due to theft:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Copy of STNK',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'Original or a legalised copy of License Plate Block letter from the police.',
                  })}
                </div>
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'Motorcycle loss due to Total Loss Accident:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Copy of STNK',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'Confirmation letter from the police',
                  })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Photo of damage to the motorcycle',
                  })}
                </div>
                <div>
                  4.{' '}
                  {formatMessage({
                    id: 'If motorcycle is covered under other motor vehicle policy, an approval claim letter from such policy can replace the above required documents.',
                  })}
                </div>
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'Personal Accident:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Copy of Death Certificate/Total Permanent Disability due to Accident from a doctor.',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'If the insured person is lost due to an accident, a copy of the Search Termination letter is required from the authorized party and a statement from the heirs to return the compensation if the insured person is found alive.',
                  })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Family card (if the beneficiary is a legal heir according to the law)',
                  })}
                </div>
              </div>
            </div>
          ),
        },
        {
          label: formatMessage({
            id: 'Legal Liability to Third Parties:',
          }),
          value: (
            <div>
              <div>
                <div>
                  1.{' '}
                  {formatMessage({
                    id: 'Letter of claim from a third party',
                  })}
                </div>
                <div>
                  2.{' '}
                  {formatMessage({
                    id: 'Copy of Police Letter regarding the accident',
                  })}
                </div>
                <div>
                  3.{' '}
                  {formatMessage({
                    id: 'Copy of Death Certificate/Total Permanent Disability Certificate due to Accident from a doctor',
                  })}
                </div>
              </div>
            </div>
          ),
        },
      ],
    },
  ],
});

export const getPolicyWordingMap = (formatMessage: any) => ({
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: [
    {
      text: formatMessage({ id: 'Policy wording' }),
      url: TRAVEL_DOMESTIC_POLICY_WORDING,
    },
  ],
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: [
    {
      text: formatMessage({ id: 'Policy wording' }),
      url: ZURICH_SYARIAH_PA_POLICY_WORDING,
    },
  ],
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: [
    {
      text: formatMessage({ id: 'Policy wording' }),
      url: PA_HOSPITAL_CASH_POLICY_WORDING,
    },
  ],
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: [
    {
      text: formatMessage({ id: 'Policy wording' }),
      url: PA_HOME_PROTECTION_POLICY_WORDING,
    },
  ],
  [PRODUCT_NAMES.MOTOLITE]: [
    {
      text: formatMessage({ id: 'Policy wording' }),
      url: MOTOLITE_POLICY_WORDING,
    },
  ],
});

export const getDeclarationMap = (formatMessage: any) => ({
  [PRODUCT_NAMES.TRAVEL_DOMESTIC]: [
    {
      name: 'agreeIgloo',
      content: formatMessage(
        {
          id: `I agree to Igloo's {termsOfService} and {privacyPolicy}.`,
        },
        {
          termsOfService: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    TERMS_OF_SERVICE,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Terms of service' })}
            </a>
          ),
          privacyPolicy: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    PRIVACY_POLICY,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Privacy policy' })}
            </a>
          ),
        },
      ),
      errorMsg: formatMessage({
        id: 'Please check the Terms of service and Privacy policy to proceed.',
      }),
    },
    {
      name: 'agreeTruth',
      content: formatMessage({
        id: `I hereby declare that all the information I have provided for the insurance policy application is true and accurate. I understand that any inaccuracies of information provided may result to the invalidation of the insurance policy.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
  ],
  [PRODUCT_NAMES.ZURICH_SYARIAH_PA]: [
    {
      name: 'agreeIgloo',
      content: formatMessage(
        {
          id: `I agree to Igloo's {termsOfService} and {privacyPolicy}.`,
        },
        {
          termsOfService: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    TERMS_OF_SERVICE,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Terms of service' })}
            </a>
          ),
          privacyPolicy: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    PRIVACY_POLICY,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Privacy policy' })}
            </a>
          ),
        },
      ),
      errorMsg: formatMessage({
        id: 'Please check the Terms of service and Privacy policy to proceed.',
      }),
    },
    {
      name: 'agreeOccupation',
      content: formatMessage({
        id: `I declare that I am not military or police officer, aircraft crew, ship crew,  professional athletes, offshore workers, underground activities worker, and underwater activities worker.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
    {
      name: 'agreeTruth',
      content: formatMessage({
        id: `I hereby declare that all the information I have provided for the insurance policy application is true and accurate. I understand that any inaccuracies of information provided may result to the invalidation of the insurance policy.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
  ],
  [PRODUCT_NAMES.PA_HOSPITAL_CASH]: [
    {
      name: 'agreeIgloo',
      content: formatMessage(
        {
          id: `I agree to Igloo's {termsOfService} and {privacyPolicy}.`,
        },
        {
          termsOfService: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    TERMS_OF_SERVICE,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Terms of service' })}
            </a>
          ),
          privacyPolicy: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    PRIVACY_POLICY,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Privacy policy' })}
            </a>
          ),
        },
      ),
      errorMsg: formatMessage({
        id: 'Please check the Terms of service and Privacy policy to proceed.',
      }),
    },
    {
      name: 'agreeOccupation',
      content: formatMessage({
        id: `I declare that I am not military or police officer, aircraft crew, ship crew,  professional athletes, offshore workers, underground activities worker, and underwater activities worker.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
    {
      name: 'agreeTruth',
      content: formatMessage({
        id: `I hereby declare that all the information I have provided for the policy application is true and accurate.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
  ],
  [PRODUCT_NAMES.PA_HOME_PROTECTION]: [
    {
      name: 'agreeIgloo',
      content: formatMessage(
        {
          id: `I agree to Igloo's {termsOfService} and {privacyPolicy}.`,
        },
        {
          termsOfService: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    TERMS_OF_SERVICE,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Terms of service' })}
            </a>
          ),
          privacyPolicy: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    PRIVACY_POLICY,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Privacy policy' })}
            </a>
          ),
        },
      ),
      errorMsg: formatMessage({
        id: 'Please check the Terms of service and Privacy policy to proceed.',
      }),
    },
    {
      name: 'agreeOccupation',
      content: formatMessage({
        id: `I declare that I am not military or police officer, aircraft crew, ship crew,  professional athletes, offshore workers, underground activities worker, and underwater activities worker.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
    {
      name: 'agreeTruth',
      content: formatMessage({
        id: `I hereby declare that all the information I have provided for the policy application is true and accurate.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
  ],
  [PRODUCT_NAMES.MOTOLITE]: [
    {
      name: 'agreeIgloo',
      content: formatMessage(
        {
          id: `I agree to Igloo's {termsOfService} and {privacyPolicy}.`,
        },
        {
          termsOfService: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    TERMS_OF_SERVICE,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Terms of service' })}
            </a>
          ),
          privacyPolicy: (
            <a
              className="text-secondary underline"
              onClick={() => {
                jump2ThirdPartyPage(
                  `${PDF_VIEWER_URL}/viewer?url=${encodeURIComponent(
                    PRIVACY_POLICY,
                  )}&downloadable=0`,
                );
              }}
            >
              {formatMessage({ id: 'Privacy policy' })}
            </a>
          ),
        },
      ),
      errorMsg: formatMessage({
        id: 'Please check the Terms of service and Privacy policy to proceed.',
      }),
    },
    {
      name: 'agreeVehicle',
      content: formatMessage({
        id: `At the time of closing the insurance, the insured vehicle is not experiencing total damage either accident or loss. If at the time the policyholder makes a claim there is evidence that the motor unit does not exist or has experienced total damage prior to the policy period, then the claim cannot be approved`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
    {
      name: 'agreeTruth',
      content: formatMessage({
        id: `I hereby declare that all the information I have provided for the policy application is true and accurate.`,
      }),
      errorMsg: formatMessage({
        id: 'Please check the declaration to proceed.',
      }),
    },
  ],
});
